import {
  AccountActivateRequest,
  AuthLogin,
  AuthRegisterRequest,
  ContactUsRequest,
  CreateUserRequest,
  ForgetPasswordRequest,
  SupportTicketRequest,
  TransferRequest,
  TwoFATokenRequest,
  UpdateEmailAddressRequest,
  UpdatePasswordRequest,
  UpdateProfileRequest,
} from "reduxStore/dataTypes";
import api from "./api";

const endPoint = {
  login: "/auth/login",
  register: "/auth/register",
  activateAccount: "/auth/activate-account",
  requestActivationEmail: "/auth/request-activation-email",
  requestResetPassword: "/auth/request-reset-password",
  resetPassword: "/auth/reset-password",
  profile: "/user/profile",
  walletDetails: "/wallet/",
  setup2FA: "/auth/setup-2fa",
  enable2FA: "/auth/enable-2fa",
  disable2FA: "/auth/disable-2fa",
  verify2FA: "/auth/verify-2fa",
  transactions: "/transactions",
  dashboard: "/dashboard",
  adminLogin: "/admin/auth/login",
  supportFAQ: "/support/faqs",
  adminUsers: "/admin/user",
  adminTransactions: "/admin/transactions",
  transactionChartVolume: "/dashboard/charts/transaction-volume",
  chartRatio: "/dashboard/charts/send-receive-ratio/yearly",
  adminWallet: "/admin/wallet",
  adminDashboard: "/admin/dashboard",
  adminTransactionChartVolume: "admin/dashboard/transaction-volume",
  adminChartRatio: "admin/dashboard/send-receive-ratio/yearly",
  transfer: "/wallet/transfer",
  updateProfile: "/user/profile",
  adminProfileUpdate: "/admin/user",
  updatePassword: "/user/update-password",
  supportTicket: "/support/ticket",
  contactUs: "/contact-us",
  mailerConfig: "/admin/settings/group",
  updateEmailAddress: "/user/update-email",
  verifyEmail: "/user/verify-email", // New endpoint for email OTP verification
  adminUserProfile: "/admin/user/profile",
  adminRegisterUser: "admin/auth/register",
};

export const login = async (body: AuthLogin) => {
  return await api.post(endPoint.login, body);
};

export const adminLogin = async (body: AuthLogin) => {
  return await api.post(endPoint.adminLogin, body);
};

export const adminCreateUser = async (body: CreateUserRequest) => {
  const response = await api.post(endPoint.adminRegisterUser, body);
  return response;
};

export const register = async (body: AuthRegisterRequest) => {
  const response = await api.post(endPoint.register, body);
  return response;
};

export const activateAccountRequest = async (body: AccountActivateRequest) => {
  const response = await api.post(endPoint.activateAccount, body);
  return response;
};

export const requestActivationEmail = async (email: string) => {
  const response = await api.post(endPoint.requestActivationEmail, { email });
  return response;
};

export const fetchProfile = async () => {
  return await api.get(endPoint.profile);
};

export const fetchWalletDetails = async () => {
  return await api.get(endPoint.walletDetails);
};

export const fetch2FACode = async () => {
  return await api.get(endPoint.setup2FA);
};

export const enable2FA = async (body: TwoFATokenRequest) => {
  return await api.patch(endPoint.enable2FA, body);
};

export const disable2FA = async (body: TwoFATokenRequest) => {
  return await api.patch(endPoint.disable2FA, body);
};

export const verify2FA = async (body: TwoFATokenRequest) => {
  return await api.post(endPoint.verify2FA, body);
};

export const fetchTransactions = async (body: string) => {
  const filteredQueryObject: Record<string, string> = Object.fromEntries(
    Object.entries(body)
      .filter(([key, value]) => value !== "")
      .map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(filteredQueryObject).toString();
  const apiString =
    `${endPoint.transactions}?${queryString}` || endPoint.transactions;
  const response = await api.get(apiString);
  return response;
};

export const fetchTransactionDetails = async (id: string) => {
  const response = await api.get(`${endPoint.transactions}/${id}`);
  return response;
};

export const requestResetPasswordApi = async (email: string) => {
  const response = await api.post(endPoint.requestResetPassword, { email });
  return response;
};

export const requestUpdatePasswordApi = async (body: ForgetPasswordRequest) => {
  const response = await api.post(endPoint.resetPassword, body);
  return response;
};

export const fetchDashboardTotalBalance = async () => {
  const response = await api.get(endPoint.dashboard);
  return response;
};

export const fetchSupportFAQ = async () => {
  const response = await api.get(endPoint.supportFAQ);
  return response;
};

export const fetchAdminUsers = async (body: any) => {
  const filteredQueryObject: Record<string, string> = Object.fromEntries(
    Object.entries(body)
      .filter(([key, value]) => value !== "")
      .map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(filteredQueryObject).toString();
  const apiString =
    `${endPoint.adminUsers}?${queryString}` || endPoint.adminUsers;
  const response = await api.get(apiString);
  return response;
};

export const fetchAdminUserDetails = async (id: string) => {
  const response = await api.get(`${endPoint.adminUsers}/${id}`);
  return response;
};

export const fetchAdminTransactions = async (body: any) => {
  const filteredQueryObject: Record<string, string> = Object.fromEntries(
    Object.entries(body)
      .filter(([key, value]) => value !== "")
      .map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(filteredQueryObject).toString();
  const apiString =
    `${endPoint.adminTransactions}?${queryString}` ||
    endPoint.adminTransactions;
  const response = await api.get(apiString);
  return response;
};

export const fetchMonthlyTransactionVolume = async () => {
  const year = new Date().getFullYear();
  const response = await api.get(`${endPoint.transactionChartVolume}/${year}`);
  return response;
};

export const fetchWeeklyRatio = async () => {
  const response = await api.get(`${endPoint.chartRatio}`);
  return response;
};

export const fetchAdminWallet = async (body: any) => {
  const filteredQueryObject: Record<string, string> = Object.fromEntries(
    Object.entries(body)
      .filter(([key, value]) => value !== "")
      .map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(filteredQueryObject).toString();
  const apiString =
    `${endPoint.adminWallet}?${queryString}` || endPoint.adminWallet;
  const response = await api.get(apiString);
  return response;
};

export const fetchAdminDashboard = async () => {
  const response = await api.get(`${endPoint.adminDashboard}`);
  return response;
};

export const fetchAdminProfile = async () => {
  const response = await api.get(`${endPoint.adminUserProfile}`);
  return response;
};

export const fetchAdminMonthlyTransactionVolume = async () => {
  const year = new Date().getFullYear();
  const response = await api.get(
    `${endPoint.adminTransactionChartVolume}/${year}`
  );
  return response;
};

export const fetchAdminWeeklyRatio = async () => {
  const response = await api.get(`${endPoint.adminChartRatio}`);
  return response;
};

export const makeTransferRequest = async (body: TransferRequest) => {
  const response = await api.post(endPoint.transfer, body);
  return response;
};

export const updateProfile = async (body: UpdateProfileRequest) => {
  const response = await api.patch(endPoint.updateProfile, body);
  return response;
};

export const userStatusUpdate = async (
  id: string | undefined,
  status: string
) => {
  const response = await api.patch(`/admin/user/${id}/${status}`);
  return response;
};

export const adminTransactionDetailsRequest = async (id: string) => {
  const response = await api.get(`${endPoint.adminTransactions}/${id}`);
  return response;
};

export const updateAdminProfile = async (id: string | undefined, body: any) => {
  const response = await api.patch(
    `${endPoint.adminProfileUpdate}/${id}`,
    body
  );
  return response;
};

export const updateUserPassword = async (body: UpdatePasswordRequest) => {
  const response = await api.post(endPoint.updatePassword, body);
  return response;
};

export const adminWalletDetails = async (body: any) => {
  const filteredQueryObject: Record<string, string> = Object.fromEntries(
    Object.entries(body)
      .filter(([key, value]) => value !== "")
      .map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(filteredQueryObject).toString();
  const apiString =
    `${endPoint.adminWallet}?${queryString}` || endPoint.adminWallet;
  const response = await api.get(apiString);
  return response;
};

export const supportTicketRequest = async (body: SupportTicketRequest) => {
  const response = await api.post(endPoint.supportTicket, body);
  return response;
};

export const postContactUs = async (body: ContactUsRequest) => {
  const response = await api.post(endPoint.contactUs, body);
  return response;
};

export const fetchMailerConfig = async (type: string) => {
  const response = await api.get(`${endPoint.mailerConfig}/${type}`);
  return response;
};

export const updateConfigGroup = async (type: string, body: any) => {
  const response = await api.patch(`${endPoint.mailerConfig}/${type}`, body);
  return response;
};

export const updateUserEmailAddress = async (
  body: UpdateEmailAddressRequest
) => {
  const response = await api.patch(endPoint.updateEmailAddress, body);
  return response;
};

export const verifyEmail = async (body: { otp: string; email: string }) => {
  const response = await api.post(endPoint.verifyEmail, body);
  return response;
};
